// =============================================================================
// Navigation - Horizontal top navigation bar (from Figma design)
// =============================================================================

import { useState, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuthStore } from '@/stores/auth';
import { useLogout } from '@/hooks/useAuth';
import { Avatar } from '@/components/ui/Avatar';
import { api } from '@/lib/api';
import { LayoutDashboard, Users, Building2, HelpCircle, LogOut, ChevronDown, Settings, TrendingUp } from 'lucide-react';

export function Navigation() {
  const { user } = useAuthStore();
  const location = useLocation();
  const logoutMutation = useLogout();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const handleLogout = () => {
    logoutMutation.mutate();
  };

  // Fetch presigned avatar URL when user has avatar
  useEffect(() => {
    async function fetchAvatarUrl() {
      if (user?.avatarUrl) {
        try {
          const response = await api.get<{ success: boolean; avatarUrl: string | null }>('/auth/avatar/url');
          if (response.avatarUrl) {
            setAvatarUrl(response.avatarUrl);
          }
        } catch (error) {
          // Silently fail - will show initials instead
          console.error('Failed to fetch avatar URL:', error);
        }
      } else {
        setAvatarUrl(null);
      }
    }
    fetchAvatarUrl();
  }, [user?.avatarUrl]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const navItems = [
    { path: '/properties', icon: Building2, label: 'Properties' },
    { path: '/buyers', icon: Users, label: 'Buyers' },
    { path: '/pipeline', icon: TrendingUp, label: 'Pipeline' },
    { path: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
    { path: '/support', icon: HelpCircle, label: 'Support' },
  ];

  return (
    <nav className="bg-white border-b border-gray-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16">
          <div className="flex">
            <div className="flex-shrink-0 flex items-center">
              <Link to="/" className="flex items-center gap-2">
                <img
                  src="/logo-1x.png"
                  alt="Transakt"
                  className="h-8 w-auto"
                  srcSet="/logo-1x.png 1x, /logo-2x.png 2x"
                />
                <span className="text-xl font-semibold bg-gradient-to-r from-[#17DDAB] to-[#009FE4] bg-clip-text text-transparent">
                  Transakt
                </span>
              </Link>
            </div>
            <div className="hidden sm:ml-8 sm:flex sm:space-x-4">
              {navItems.map((item) => {
                const Icon = item.icon;
                const isActive = item.path === '/'
                  ? location.pathname === '/'
                  : location.pathname.startsWith(item.path);

                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`inline-flex items-center px-3 py-2 text-sm ${
                      isActive
                        ? 'border-b-2 border-[#009FE4]'
                        : 'text-gray-700 hover:text-[#009FE4]'
                    }`}
                  >
                    <Icon className={`w-4 h-4 mr-2 ${
                      isActive ? 'text-[#009FE4]' : ''
                    }`} />
                    <span className={isActive ? 'bg-gradient-to-r from-[#17DDAB] to-[#009FE4] bg-clip-text text-transparent' : ''}>
                      {item.label}
                    </span>
                  </Link>
                );
              })}
            </div>
          </div>

          <div className="flex items-center">
            {/* User dropdown */}
            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                className="flex items-center space-x-2 px-3 py-2 text-sm text-gray-700 hover:text-[#009FE4] rounded-md hover:bg-gray-50 transition-colors"
              >
                <Avatar
                  src={avatarUrl}
                  firstName={user?.firstName}
                  lastName={user?.lastName}
                  size="sm"
                />
                <span>{user?.firstName} {user?.lastName}</span>
                <ChevronDown className={`w-4 h-4 transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`} />
              </button>

              {/* Dropdown menu */}
              {isDropdownOpen && (
                <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg py-1 border border-gray-200 z-50">
                  <Link
                    to="/settings"
                    onClick={() => setIsDropdownOpen(false)}
                    className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-[#009FE4]"
                  >
                    <Settings className="w-4 h-4 mr-3" />
                    Settings
                  </Link>
                  <hr className="my-1 border-gray-200" />
                  <button
                    onClick={() => {
                      setIsDropdownOpen(false);
                      handleLogout();
                    }}
                    className="w-full flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-red-600"
                  >
                    <LogOut className="w-4 h-4 mr-3" />
                    Logout
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Mobile navigation */}
      <div className="sm:hidden">
        <div className="pt-2 pb-3 space-y-1">
          {navItems.map((item) => {
            const Icon = item.icon;
            const isActive = item.path === '/'
                  ? location.pathname === '/'
                  : location.pathname.startsWith(item.path);

            return (
              <Link
                key={item.path}
                to={item.path}
                className={`block pl-3 pr-4 py-2 border-l-4 text-base ${
                  isActive
                    ? 'bg-gradient-to-r from-[#17DDAB]/10 to-[#009FE4]/10 border-[#009FE4] text-[#009FE4]'
                    : 'border-transparent text-gray-700 hover:bg-gray-50 hover:border-gray-300'
                }`}
              >
                <div className="flex items-center">
                  <Icon className="w-5 h-5 mr-3" />
                  {item.label}
                </div>
              </Link>
            );
          })}

          {/* Mobile user menu */}
          <div className="border-t border-gray-200 pt-4 pb-3">
            <div className="flex items-center px-4 mb-3">
              <Avatar
                src={avatarUrl}
                firstName={user?.firstName}
                lastName={user?.lastName}
                size="md"
                className="mr-3"
              />
              <div className="text-base text-gray-700">
                {user?.firstName} {user?.lastName}
              </div>
            </div>
            <Link
              to="/settings"
              className="block pl-3 pr-4 py-2 border-l-4 border-transparent text-base text-gray-700 hover:bg-gray-50 hover:border-gray-300"
            >
              <div className="flex items-center">
                <Settings className="w-5 h-5 mr-3" />
                Settings
              </div>
            </Link>
            <button
              onClick={handleLogout}
              className="w-full block pl-3 pr-4 py-2 border-l-4 border-transparent text-base text-left text-gray-700 hover:bg-gray-50 hover:border-gray-300 hover:text-red-600"
            >
              <div className="flex items-center">
                <LogOut className="w-5 h-5 mr-3" />
                Logout
              </div>
            </button>
          </div>
        </div>
      </div>
    </nav>
  );
}
